import { useCallback } from 'react';
import { useCreateBankAddress } from '../api';
import { useToast } from '@/shared/lib';
import { mapFieldsToRequestParams } from './mapFieldsToRequestParams';
import type { UseFormReturn } from 'react-hook-form';
import type { CreateBankAddressFormValues } from './types';
import type {
  BankAddressField,
  BankAddressStepValues,
} from '@/entities/bank-address';
import { useTranslation } from 'react-i18next';

type Props = {
  form: UseFormReturn<CreateBankAddressFormValues>;
  fields: BankAddressField[];
  isLastStep: boolean;
  onSuccessCreateBankAddress: () => void;
  submitStep: (
    values: BankAddressStepValues
  ) => Promise<BankAddressStepValues | undefined>;
};

export const useCreateBankAddressFormLogic = ({
  form,
  fields,
  isLastStep,
  onSuccessCreateBankAddress,
  submitStep,
}: Props) => {
  const { toast } = useToast();
  const { t } = useTranslation();

  const { mutateAsync: createBankAddress, isPending } = useCreateBankAddress({
    onSuccess: () => {
      toast.success(t('bankAddress.successToastMessage'));
    },
    onError: error => {
      toast.error(error.message || t('bankAddress.errorToastMessage'));
    },
  });

  const submitBankAddress = useCallback(async () => {
    const isValid = await form.trigger();
    if (!isValid) return;

    const values = form.getValues();

    if (isLastStep) {
      try {
        const requestParams = mapFieldsToRequestParams(fields, values);
        await createBankAddress(requestParams);
        onSuccessCreateBankAddress();
      } catch (error) {
        console.error(t('bankAddress.errorToastMessage'), error);
      }
    } else {
      const nextStepValues = await submitStep(values);
      if (nextStepValues) {
        form.reset(nextStepValues);
      }
    }
  }, [
    form,
    isLastStep,
    fields,
    createBankAddress,
    onSuccessCreateBankAddress,
    submitStep,
  ]);

  return {
    submitBankAddress,
    isPending,
  };
};
